import * as React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { JobRun, JobRunColumn } from 'services/jobSpecs';

/**
 * Run history for any job.
 *
 * Columns are supplied rather than inferred: a status wants a chip, a timestamp
 * wants formatting, and an id wants neither. Every new job gets a history table
 * by declaring its columns.
 */

type Props<T extends JobRun> = {
  columns: JobRunColumn<T>[];
  rows: T[];
  loading?: boolean;
  empty?: string;
  /** highlighted while a just-submitted run is being waited on */
  highlightKey?: (row: T) => boolean;
};

const JobRunTable = <T extends JobRun>({
  columns,
  rows,
  loading,
  empty = 'No runs yet.',
  highlightKey,
}: Props<T>): React.ReactElement => {
  if (loading && rows.length === 0) {
    return <Typography variant="body2">Loading runs…</Typography>;
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {empty}
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column.key}>{column.label}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow
            key={String(row.id ?? index)}
            sx={
              highlightKey?.(row)
                ? { backgroundColor: 'rgba(25,118,210,0.08)' }
                : undefined
            }
          >
            {columns.map((column) => (
              <TableCell key={column.key}>
                {column.render
                  ? column.render(row)
                  : String(row[column.key] ?? '—')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default JobRunTable;
